import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  useLazyGetUserQuery,
  usePostSignUpMutation,
  usePostSocialLoginMutation,
} from "../../services/auth";
import {
  Loader,
  STORAGE_KEYS,
  getFromStorage,
  setToStorage,
  showError,
} from "../../constants";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import app from "../../utils/firebaseKeys";
import { useAppDispatch } from "../../hooks/store";
import { setCredentials, temporaryToken } from "../../reducers/authSlice";
import useTranslation from "../../hooks/Translation";

const provider = new GoogleAuthProvider();

const SignUp = () => {
  const translation = useTranslation() as any;

  const auth = getAuth(app);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [signUpMutation, { isLoading }] = usePostSignUpMutation();
  const [socialLoginMutation, socialData] = usePostSocialLoginMutation();
  const [getUser] = useLazyGetUserQuery();
  const [email, setEmail] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);

  const fcm_token = getFromStorage(STORAGE_KEYS.fcmToken);

  const getUserDetails = async () => {
    const token = getFromStorage(STORAGE_KEYS.token);
    try {
      const result = await getUser({}).unwrap();
      if (result?.statusCode === 200 && token) {
        dispatch(
          setCredentials({
            user: result?.data,
            token: token,
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const deviceId = navigator.userAgent;
      const data = {
        email: user?.email,
        firstName: user?.displayName?.split(" ")?.[0] || "",
        lastName: user?.displayName?.split(" ")?.[1] || "",
        image: user?.photoURL,
        socialId: user?.uid,
        socialType: "GOOGLE",
        deviceType: "WEB",
        deviceToken: fcm_token,
      };
      try {
        const response = await socialLoginMutation(data).unwrap();
        if (response?.statusCode === 200) {
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          dispatch(
            setCredentials({
              user: response?.data,
              token: response?.data?.token || "",
            })
          );
          dispatch(
            temporaryToken({
              tempToken: null,
            })
          );
          if (response?.data?.isProfileComplete) {
            navigate("/dashboard", { replace: true });
          } else if (!response?.data?.isProfileComplete) {
            const state = {
              key: response?.data?.isEmailVerify
                ? response?.data?.email
                : response?.data?.phone,
              page: "login",
              type: response?.data?.isEmailVerify ? "email" : "phone",
              ...(!response?.data?.isEmailVerify
                ? { countryCode: response?.data?.countryCode }
                : {}),
              ...(!response?.data?.isEmailVerify
                ? { countryName: response?.data?.countryName }
                : {}),
            };
            navigate("/profile-setup", { state: state, replace: true });
          }
          getUserDetails();
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
        console.log(error);
      }
    } catch (error) {
      console.error("Google Login Error:", error);
    }
  };
  //-----google login---//

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      passwordConfirmation: "",
      phone: "",
      countryCode: "+965",
      countryName: "KW",
    },
    validationSchema: Yup.object({
      email: email
        ? Yup.string()
            .required(translation.error.emailReq)
            .matches(
              /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
              translation.error.validEmail
            )
        : Yup.string().notRequired(),
      password: email
        ? Yup.string()
            .label("Password")
            .required(translation.error.passwordReq)
            .min(6, translation.error.passReq)
            .matches(
              /(?=^.{8,}$)(?=.*\d)(?=.*[!@#$%^&*]+)(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/,
              translation.auth.passValid
            )
        : Yup.string().notRequired(),

      passwordConfirmation: email
        ? Yup.string()
            .oneOf([Yup.ref("password")], translation.auth.passMatch)
            .required(translation.auth.confirmPasswordReq)
        : Yup.string().notRequired(),
      phone: email
        ? Yup.string().notRequired()
        : Yup.string()
            .required(translation.error.phoneReq)
            .min(6, translation.error.minPhone)
            .max(16, translation.error.maxPhone),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);
      let body = {
        key: email ? formik.values.email : formik.values.phone,
        ...(email ? { password: formik.values.password } : {}),
        ...(!email ? { countryCode: formik.values.countryCode } : {}),
        ...(!email ? { countryName: formik.values.countryName } : {}),
      };
      try {
        const response = await signUpMutation(body).unwrap();
        if (response?.statusCode === 200) {
          const data = {
            key: body?.key,
            page: "signUp",
            type: email ? "email" : "phone",
            ...(!email ? { countryCode: formik.values.countryCode } : {}),
            ...(!email ? { countryName: formik.values.countryName } : {}),
          };

          navigate("/verify-otp", { state: data });
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
      }
    },
  });

  // for country picker
  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    formik.setFieldValue(
      "countryCode",
      country?.dialCode.includes("+") ? "" : "+" + country?.dialCode
    );
    formik.setFieldValue("countryName", country?.countryCode.toUpperCase());
  };

  return (
    <div className="authh">
      <Loader isLoad={isLoading || socialData?.isLoading} />
      <div className="conta_iner">
        <div className="auth_parnt">
          <div style={{ paddingTop: 80 }} className="auth_left">
            <figure className="authLogo">
              <img src="/static/images/logo.png" alt="" />
            </figure>
            <h2>{translation.auth.createAcc}</h2>
            <div className="google_div">
              {email ? (
                <img
                  onClick={() => setEmail(false)}
                  className="google_icon"
                  src="/static/images/call.png"
                  alt=""
                />
              ) : (
                <img
                  onClick={() => setEmail(true)}
                  className="email_fig"
                  src="/static/images/email.png"
                  alt=""
                />
              )}

              <img
                onClick={signInWithGoogle}
                className="google_icon"
                src="/static/images/google.png"
                alt=""
              />
              <img
                className="google_icon"
                src="/static/images/facebook.png"
                alt=""
              />
            </div>
            <h5>
              {translation.auth.usePhone}{" "}
              {email ? translation.auth.email : translation.auth.phone}{" "}
              {translation.auth.register}{" "}
            </h5>
            <form onSubmit={formik.handleSubmit}>
              <div className="auth_form">
                {email ? (
                  <>
                    <FormControl sx={{ mb: 2, width: "100%" }}>
                      <Typography className="custom_label">
                        {translation.auth.emailId}
                      </Typography>
                      <TextField
                        className="text_field"
                        hiddenLabel
                        placeholder={translation.auth.emailId}
                        fullWidth
                        name="email"
                        type="email"
                        variant="outlined"
                        id="email"
                        onChange={(val) => {
                          if (
                            val.target.value === " " ||
                            val.target.value === "."
                          ) {
                          } else {
                            formik.handleChange(val);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        helperText={formik.touched.email && formik.errors.email}
                      />
                    </FormControl>
                    <FormControl sx={{ mb: 2, width: "100%" }}>
                      <Typography className="custom_label">
                        {translation.auth.password}
                      </Typography>
                      <TextField
                        className="text_field"
                        hiddenLabel
                        placeholder={translation.auth.password}
                        fullWidth
                        name="password"
                        id="password"
                        onChange={(val) => {
                          if (
                            val.target.value === " " ||
                            val.target.value === "."
                          ) {
                          } else {
                            formik.handleChange(val);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                        helperText={
                          formik.touched.password && formik.errors.password
                        }
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment className="eye_btn" position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                onMouseDown={() =>
                                  setShowPassword(!showPassword)
                                }
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                    <FormControl sx={{ mb: 2, width: "100%" }}>
                      <Typography className="custom_label">
                        {translation.auth.confmPas}
                      </Typography>
                      <TextField
                        className="text_field"
                        hiddenLabel
                        placeholder={translation.auth.confmPas}
                        fullWidth
                        name="passwordConfirmation"
                        id="passwordConfirmation"
                        onChange={(val) => {
                          if (
                            val.target.value === " " ||
                            val.target.value === "."
                          ) {
                          } else {
                            formik.handleChange(val);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.passwordConfirmation}
                        helperText={
                          formik.touched.passwordConfirmation &&
                          formik.errors.passwordConfirmation
                        }
                        type={confirmShowPassword ? "text" : "password"}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment className="eye_btn" position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  setConfirmShowPassword(!confirmShowPassword)
                                }
                                onMouseDown={() => !confirmShowPassword}
                                edge="end"
                              >
                                {confirmShowPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  </>
                ) : (
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <Typography className="custom_label">
                      {translation.auth.phone}
                    </Typography>
                    <PhoneInput
                      enableSearch
                      value={formik.values.countryCode + formik.values.phone}
                      country={"us"}
                      inputClass="phoneInput"
                      inputStyle={{ width: "100%" }}
                      buttonClass="phoneBtn"
                      placeholder={translation.auth.phone}
                      onChange={(phone, country) =>
                        handleChangePhone(phone, country)
                      }
                      onBlur={formik.handleBlur}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault(); // Prevent default behavior (form submission)
                          formik.handleSubmit(); // Manually submit the form
                        }
                      }}
                    />
                    {formik.touched.phone && formik.errors.phone ? (
                      <h6 className="err_msg">
                        {formik.touched.phone && formik.errors.phone}
                      </h6>
                    ) : (
                      ""
                    )}
                  </Grid>
                )}

                <Box sx={{ pt: 2, mb: 4 }}>
                  <Button
                    className="btn btn_primary"
                    color="primary"
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {translation.auth.signUp}
                  </Button>
                </Box>
              </div>
            </form>
          </div>
          <div className="auth_ryt">
            <div className="absolute_div">
              <h3>{translation.auth.welcome}</h3>
              <p>{translation.auth.already}</p>
              <Box sx={{ pt: 3, display: "flex", justifyContent: "center" }}>
                <Button
                  className="btn"
                  color="primary"
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={() => navigate("/")}
                >
                  {translation.auth.login}
                </Button>
              </Box>
            </div>
            <figure>
              <img src="/static/images/cook1.jpg" alt="" />
            </figure>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
